import React, { useState } from "react";
import type { ImageFile } from "../App";
import { EditModal } from "./EditModal";

interface ImagesProps {
  images: ImageFile[];
  onDelete: (id: number) => void;
  onRetry?: (id: number) => void;
  onViewDiagnostics?: () => void;
}

export function Images({ images, onDelete, onRetry, onViewDiagnostics }: ImagesProps) {
  return (
    <div>
      <h2 className="hidden text-gray-800 text-xl font-semibold mb-4">Images: {images.length}</h2>
      <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {images.map((image) => {
          if(image.file.type.includes("video")) {
            return <Video video={image} key={image.id} />;
          } else {
            return <ImageSpot image={image} onDelete={onDelete} onRetry={onRetry} onViewDiagnostics={onViewDiagnostics} key={image.id} />;
          }
        })}
      </div>
    </div>
  );
}

function Video({ video }: { video: ImageFile }) {
  const url = URL.createObjectURL(video.file);
  return (
    <div className="bg-white rounded-lg shadow-md p-3">
      <video
        className="rounded-lg aspect-square object-cover"
        loop
        muted
        autoPlay
        src={url}
      ></video>
    </div>
  );
}

interface ImageSpotProps {
  image: ImageFile;
  onDelete: (id: number) => void;
  onRetry?: (id: number) => void;
  onViewDiagnostics?: () => void;
}

type CopyState = "idle" | "copied" | "failed";

function formatBytes(bytes?: number): string {
  if (typeof bytes !== "number") return "unknown";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function diagnosticRows(image: ImageFile): Array<[string, string]> {
  const details = image.errorDetails;
  const imageInfo = details?.image;
  const dimensions = imageInfo?.width && imageInfo?.height
    ? `${imageInfo.width} × ${imageInfo.height}`
    : "unknown";

  return [
    ["Failed phase", details?.failedPhase ?? "unknown"],
    ["Elapsed", typeof details?.elapsedMs === "number" ? `${details.elapsedMs} ms` : "unknown"],
    ["Last completed", details?.lastCompletedStep ?? "unknown"],
    ["Model", details?.modelSelected ?? "unknown"],
    ["isIOS", typeof details?.isIOS === "boolean" ? String(details.isIOS) : "unknown"],
    ["crossOriginIsolated", typeof details?.crossOriginIsolated === "boolean" ? String(details.crossOriginIsolated) : "unknown"],
    ["SharedArrayBuffer", details?.sharedArrayBufferType ?? "unknown"],
    ["Image dimensions", dimensions],
    ["File size", formatBytes(imageInfo?.fileSize ?? image.file.size)],
    ["User agent", details?.userAgent ?? navigator.userAgent],
  ];
}

function ImageSpot({ image, onDelete, onRetry, onViewDiagnostics }: ImageSpotProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState("");
  const [copyState, setCopyState] = useState<CopyState>("idle");

  const url = URL.createObjectURL(image.file);
  const processedURL = image.processedFile ? URL.createObjectURL(image.processedFile) : "";
  const hasError = Boolean(image.error);
  const isProcessed = Boolean(image.processedFile);
  const isProcessing = !isProcessed && !hasError;

  const handleEditSave = (editedImageUrl: string) => {
    setProcessedImageUrl(editedImageUrl);
  };

  const handleCopyDiagnostics = async () => {
    try {
      const text = image.errorReport || JSON.stringify({
        message: image.error,
        details: image.errorDetails,
        file: {
          name: image.file.name,
          type: image.file.type,
          size: image.file.size,
        },
      }, null, 2);
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    } finally {
      setTimeout(() => setCopyState("idle"), 2500);
    }
  };

  const transparentBg = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="relative">
        {hasError ? (
          <div className="relative">
            <img
              className="w-full aspect-square object-cover opacity-40 transition-opacity duration-200"
              src={url}
              alt={`Failed to process image ${image.id}`}
            />
            <div className="absolute inset-0 flex items-center justify-center p-3">
              <div className="bg-red-600 bg-opacity-95 rounded-lg p-3 max-w-full text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <svg className="w-4 h-4 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-white font-semibold text-sm">Processing failed</span>
                </div>
                <p className="text-white text-xs leading-snug break-words">{image.error}</p>
              </div>
            </div>
          </div>
        ) : isProcessing ? (
          <div className="relative">
            <img
              className="w-full aspect-square object-cover opacity-50 transition-opacity duration-200"
              src={url}
              alt={`Processing image ${image.id}`}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                <span className="text-white font-medium">Processing...</span>
              </div>
            </div>
          </div>
        ) : (
          <div 
            className="w-full aspect-square"
            style={{ 
              background: transparentBg,
              backgroundRepeat: 'repeat'
            }}
          >
            <img
              className="w-full h-full object-cover transition-opacity duration-200"
              src={processedImageUrl || processedURL}
              alt={`Processed image ${image.id}`}
            />
          </div>
        )}
      </div>

      {hasError && (
        <div className="p-3 border-t border-gray-100">
          <div className="mb-3 rounded-md border border-red-100 bg-red-50 p-2 text-xs text-red-950">
            <div className="font-semibold mb-1">Failure diagnostics</div>
            <dl className="space-y-1">
              {diagnosticRows(image).map(([label, value]) => (
                <div key={label} className="grid grid-cols-[7rem_1fr] gap-2">
                  <dt className="font-medium text-red-800">{label}</dt>
                  <dd className="break-words font-mono text-[11px] leading-snug">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="flex justify-center gap-2 flex-wrap">
            <button
              onClick={handleCopyDiagnostics}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
              title="Copy diagnostics"
            >
              <svg className="w-4 h-4 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-sm text-red-800">
                {copyState === "copied"
                  ? "Copied"
                  : copyState === "failed"
                    ? "Copy failed"
                    : "Copy diagnostics"}
              </span>
            </button>
            <button
              onClick={() => onDelete(image.id)}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Dismiss"
            >
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-sm text-gray-700">Dismiss</span>
            </button>
            {onRetry && (
              <button
                onClick={() => onRetry(image.id)}
                className="flex items-center gap-1 px-3 py-1.5 bg-brand-600 border border-brand-600 rounded-md hover:bg-brand-700 transition-colors"
                title="Retry"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                <span className="text-sm text-white">Retry</span>
              </button>
            )}
          </div>
          {onViewDiagnostics && (
            <div className="flex justify-center mt-2">
              <button
                onClick={onViewDiagnostics}
                className="text-xs text-red-700 underline"
              >
                View diagnostics &rarr;
              </button>
            </div>
          )}
        </div>
      )}

      {isProcessed && (
        <div className="p-3 border-t border-gray-100">
          <div className="flex justify-center gap-2">
            <button
              onClick={() => onDelete(image.id)}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Delete"
            >
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-sm text-gray-700">Delete</span>
            </button>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Edit"
            >
              <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="text-sm text-gray-700">Edit</span>
            </button>
            <a
              href={processedImageUrl || processedURL}
              download={`processed-${image.id}.png`}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Download"
            >
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="text-sm text-gray-700">Download</span>
            </a>
          </div>
        </div>
      )}

      <EditModal
        image={image}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
      />
    </div>
  );
}
